/**
 * CommonApplyDialog — 공통 조건 반영 창(설계 FE-20260913 §3 · 와이어프레임 W2).
 *
 * 미리보기(행마다 빠지는 · 더해지는 · 유지되는 항) → [N개 행에 반영] → 한 행씩 저장 진행 → 결과(성공 · 실패 · 멈춤)
 * → 실패한 행만 다시 반영. 되돌리기 수단이 없는 일괄 쓰기라 결과를 행 단위로 먼저 보인다.
 *
 *   mode 'save'  — 상위 행을 저장하면서 반영한다. 먼저 onSaveParent() 가 참이어야 하위 행을 쓴다.
 *                  [이 행만 저장]도 둔다 — 그러면 하위 행에 남은 이전 공통 항은 다음 반영 때 자동으로 바뀌지 않는다(창에 알림).
 *   mode 'apply' — 저장된 공통 조건을 하위 행에 다시 건다(새로 만든 하위 행 등).
 * 진행 중에는 닫지 않는다 — [멈춤]만. 409 로 멈추면 최신본을 확인한 뒤 창을 다시 여는 길만 둔다(계획이 낡았다).
 */
import { useMemo, useRef, useState } from 'react';
import { Button, Modal } from '../common';
import { FIELDS, describeTerm } from './syllabusQuery';
import { termDiff, writableRows } from './commonConditions';
import MathName from './MathName';

const STATUS_TEXT = {
  added: '새로 걸림', changed: '바뀜', partial: '일부 적용', unchanged: '변화 없음', skipped: '건너뜀',
};
const STATUS_CLS = {
  added: 'border-green-300 text-green-700', changed: 'border-green-300 text-green-700', partial: 'border-amber-300 text-amber-700',
  unchanged: 'border-gray-200 text-gray-500', skipped: 'border-red-200 text-red-600',
};
const STEP_TEXT = {
  saving: '저장 중', done: '저장됨 ✓', failed: '실패', halted: '멈춤', skipped: '건너뜀(저장 전 변경)',
};
const pill = 'inline-flex shrink-0 items-center rounded-full border bg-white px-2 py-0.5 text-[10px] font-medium';
const chipBase = 'inline-flex max-w-full items-center gap-1 truncate rounded-full border px-2 py-0.5 text-[11px]';

function skipReason(row) {
  if (row.reason === 'draft') return '저장 전 변경이 있어 건너뜀 — 저장하거나 되돌린 뒤 다시 반영하세요.';
  const r = row.reason?.reason;
  if (r === 'expr_too_large') return `건너뜀 — 합치면 한도를 넘습니다(${row.reason.message}).`;
  if (r === 'contradiction') return `건너뜀 — 합치면 서로 맞지 않는 조건이 됩니다(${row.reason.message}).`;
  return row.reason?.message ? `건너뜀 — ${row.reason.message}` : '건너뜀';
}

function TermChips({ row, names }) {
  if (row.status === 'skipped' || !row.next) return null;
  const diff = termDiff(row.current, row.next);
  const text = (t) => describeTerm(t, names);
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {diff.removed.map((t) => <span key={`r${text(t)}`} className={`${chipBase} border-red-200 bg-red-50 text-red-700 line-through`} title="빠지는 이전 공통 항"><MathName text={text(t)} compact /></span>)}
      {diff.added.map((t) => <span key={`a${text(t)}`} className={`${chipBase} border-green-300 bg-green-50 text-green-700`} title="더해지는 공통 항">+ <MathName text={text(t)} compact /></span>)}
      {diff.kept.map((t) => <span key={`k${text(t)}`} className={`${chipBase} border-gray-200 bg-white text-gray-600`}><MathName text={text(t)} compact /></span>)}
    </div>
  );
}

export default function CommonApplyDialog({
  open, mode = 'apply', parentName, plan, names, onClose, onSaveParent, onSaveOnly, onApplyRows,
}) {
  const [phase, setPhase] = useState('preview'); // preview · running · done · saved
  const [steps, setSteps] = useState({}); // id → 'saving'|'done'|'failed'|'halted'|'skipped'
  const [summary, setSummary] = useState(null); // { done, failed, left, halted }
  const [error, setError] = useState(null);
  const stopRef = useRef(false);
  const rows = useMemo(() => plan || [], [plan]);
  const writable = useMemo(() => writableRows(rows), [rows]);
  const count = (s) => rows.filter((r) => r.status === s).length;
  const running = phase === 'running';

  const run = async (targets, { saveFirst = false } = {}) => {
    stopRef.current = false;
    setError(null);
    setPhase('running');
    if (saveFirst) {
      const ok = await onSaveParent();
      if (!ok) {
        setPhase('preview');
        setError(<>「<MathName text={parentName} compact />」을(를) 저장하지 못해 하위 행에 반영하지 않았습니다.</>);
        return;
      }
    }
    const r = await onApplyRows(targets, {
      onStep: (id, s) => setSteps((m) => ({ ...m, [id]: s })),
      shouldStop: () => stopRef.current,
    });
    setSummary(r);
    setPhase('done');
  };

  const saveOnly = async () => {
    setError(null);
    setPhase('running');
    const ok = await onSaveOnly();
    if (!ok) { setPhase('preview'); setError(<>「<MathName text={parentName} compact />」을(를) 저장하지 못했습니다.</>); return; }
    setPhase('saved');
  };

  const retryRows = summary ? rows.filter((r) => summary.failed.some((f) => f.id === r.id)) : [];
  const leftRows = summary && !summary.halted ? rows.filter((r) => summary.left.includes(r.id) && steps[r.id] !== 'skipped') : [];
  const done = Object.values(steps).filter((s) => s === 'done' || s === 'failed' || s === 'halted' || s === 'skipped').length;

  let footer;
  if (phase === 'preview') {
    footer = (
      <div className="flex w-full flex-wrap items-center gap-2">
        <span className="text-xs text-gray-500">반영 {writable.length} · 건너뜀 {count('skipped')} · 변화 없음 {count('unchanged')}</span>
        <span className="flex-1" />
        {mode === 'save' && <Button size="sm" variant="ghost" onClick={saveOnly}>이 행만 저장</Button>}
        <Button size="sm" variant="secondary" onClick={onClose}>취소</Button>
        {mode === 'save' && !writable.length ? (
          <Button size="sm" onClick={saveOnly}>저장</Button>
        ) : (
          <Button size="sm" onClick={() => run(writable, { saveFirst: mode === 'save' })} disabled={!writable.length}>
            {writable.length}개 행에 반영
          </Button>
        )}
      </div>
    );
  } else if (running) {
    footer = (
      <div className="flex w-full items-center gap-2">
        <span role="status" aria-live="polite" className="flex-1 text-xs text-gray-600">{done} / {writable.length || rows.length} 처리</span>
        <Button size="sm" variant="secondary" onClick={() => { stopRef.current = true; }}>멈춤</Button>
      </div>
    );
  } else if (phase === 'saved') {
    footer = (
      <div className="flex w-full items-center gap-2">
        <p role="status" className="flex-1 text-xs text-amber-700">「<MathName text={parentName} compact />」만 저장했습니다. 하위 행에 남은 이전 공통 조건은 공통 표시가 사라지고, 다음 반영 때 자동으로 바뀌지 않습니다.</p>
        <Button size="sm" onClick={onClose}>닫기</Button>
      </div>
    );
  } else {
    const bad = summary?.failed.length || 0;
    footer = (
      <div className="flex w-full flex-wrap items-center gap-2">
        <p role="status" className={`flex-1 text-xs ${bad || summary?.halted ? 'text-amber-700' : 'text-green-700'}`}>
          {summary?.done.length || 0}개 행 저장{bad ? ` · ${bad}개 실패` : ''}
          {summary?.halted ? ' · 다른 곳에서 강의가 바뀌어 멈췄습니다. 최신본을 확인한 뒤 다시 반영하세요.' : ''}
          {leftRows.length ? ` · 멈춤으로 ${leftRows.length}개 행은 그대로 둠` : ''}
        </p>
        {!summary?.halted && retryRows.length > 0 && <Button size="sm" onClick={() => run(retryRows)}>실패한 행 다시 반영</Button>}
        {!summary?.halted && !retryRows.length && leftRows.length > 0 && <Button size="sm" onClick={() => run(leftRows)}>남은 행 반영</Button>}
        <Button size="sm" variant="secondary" onClick={onClose}>닫기</Button>
      </div>
    );
  }

  return (
    <Modal
      isOpen={open}
      onClose={running ? () => {} : onClose}
      title={<>「<MathName text={parentName} />」 공통 조건을 하위 {rows.length}개 행에 반영</>}
      size="lg"
      footer={footer}
      showCloseButton={!running}
      closeOnBackdropClick={!running}
      closeOnEsc={!running}
    >
      <div className="space-y-2">
        <p className="text-xs text-gray-500">
          공통 조건은 범위를 한정합니다 — 하위 행의 조건에 「그리고」로 더하고, 난이도처럼 값이 하나인 조건을 하위 행이 따로 정해 두었으면 그 값을 둡니다.
          한 행씩 저장합니다.
        </p>
        {error && <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>}
        <ul aria-label="반영할 하위 행" className="max-h-[50vh] divide-y divide-gray-100 overflow-y-auto">
          {rows.map((row) => (
            <li key={row.id} className={`py-2 ${row.status === 'skipped' ? 'opacity-70' : ''}`} style={{ paddingLeft: `${(row.depth - 1) * 16}px` }}>
              <div className="flex flex-wrap items-center gap-1.5">
                <b className="text-sm text-gray-900"><MathName text={row.name} compact /></b>
                <span className={`${pill} ${STATUS_CLS[row.status] || ''}`}>{STATUS_TEXT[row.status] || row.status}</span>
                {steps[row.id] && <span className={`${pill} ${steps[row.id] === 'done' ? 'border-green-300 text-green-700' : 'border-amber-300 text-amber-700'}`}>{STEP_TEXT[steps[row.id]]}</span>}
              </div>
              <TermChips row={row} names={names} />
              {row.status === 'partial' && row.skipped.length > 0 && (
                <p className="mt-0.5 text-[11px] text-gray-500">{row.skipped.map((f) => FIELDS[f]?.label || f).join(' · ')} — 이 행이 따로 정해 두어 이 행 값을 유지합니다.</p>
              )}
              {row.status === 'skipped' && <p className="mt-0.5 text-[11px] text-red-600">{skipReason(row)}</p>}
              {summary?.failed.some((f) => f.id === row.id) && (
                <p className="mt-0.5 text-[11px] text-red-600">{summary.failed.find((f) => f.id === row.id).message}</p>
              )}
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
